import { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertTriangle, ArrowRight, CheckCircle2, Loader2, Search } from 'lucide-react';
import Button from '../ui/Button';
import cultivarMoveService from '../../services/cultivarMove';
import type { MovableCultivar, MoveOrganisation, MoveResult } from '../../services/cultivarMove';
import { ApiError } from '../../types';
import { cn } from '../../utils/cn';

/**
 * Super Admin → Move cultivars. For cultivars registered under the wrong
 * organisation: choose where they are, tick them, choose where they go,
 * preview, move. The server decides what can move (not a legacy code the new
 * organisation already has) and what the old organisation keeps, licences and
 * orders included; this page shows its answer before anything changes.
 */

function errorText(e: unknown): string {
  if (e instanceof ApiError) return e.message;
  if (e instanceof Error) return e.message;
  return 'Something went wrong';
}

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

function MoveSummary({ result, done }: { result: MoveResult; done: boolean }) {
  const moved = result.moved.length;
  return (
    <div className="space-y-3 rounded-xl border border-neutral-200 bg-white p-4 text-sm min-w-0">
      <p className={cn('font-medium break-words', done ? 'text-emerald-800' : 'text-neutral-900')}>
        {done ? (
          <><CheckCircle2 className="inline h-4 w-4 mr-1 -mt-0.5" />Moved {plural(moved, 'cultivar')} from {result.from.name} to {result.to.name}.</>
        ) : (
          <>{plural(moved, 'cultivar')} will move from {result.from.name} to {result.to.name}.</>
        )}
      </p>

      {result.programs_unlinked > 0 && (
        <p className="text-neutral-700 break-words">
          {plural(result.programs_unlinked, 'link')} to {result.from.name}'s programs {done ? 'were' : 'will be'} removed.
          A cultivar can only be in its own organisation's programs.
        </p>
      )}

      {result.open > 0 && (
        <p className={cn('break-words', result.make_private ? 'text-neutral-700' : 'text-amber-900')}>
          {result.make_private
            ? `${plural(result.made_private, 'open cultivar')} ${done ? (result.made_private === 1 ? 'was' : 'were') : 'will be'} made private, so ${result.from.name} no longer sees ${result.made_private === 1 ? 'it' : 'them'}.`
            : `${plural(result.open, 'cultivar')} ${result.open === 1 ? 'is' : 'are'} open, so every organisation, ${result.from.name} included, still sees ${result.open === 1 ? 'it' : 'them'} after the move.`}
        </p>
      )}

      {result.related.length > 0 && (
        <div className="min-w-0">
          <p className="text-neutral-700 break-words">
            {result.move_related
              ? `${result.from.name}'s own records about them ${done ? 'moved' : 'move'} too:`
              : `${result.from.name}'s own records about them stay with ${result.from.name}:`}
          </p>
          <ul className="list-disc pl-5 text-neutral-700">
            {result.related.map(r => <li key={r.table} className="break-words">{r.label}: {r.rows}</li>)}
          </ul>
        </div>
      )}

      {result.left_behind.length > 0 && (
        <div className="rounded border border-amber-200 bg-amber-50 p-2 text-amber-900 min-w-0">
          <p className="font-medium break-words">{result.from.name} keeps these, which name the cultivars:</p>
          <ul className="list-disc pl-5">
            {result.left_behind.map(r => (
              <li key={r.table} className="break-words">
                {r.label}: {r.rows} ({plural(r.cultivars, 'cultivar')}){r.commercial ? ` — ${result.from.name} stays the party on these` : ''}
              </li>
            ))}
          </ul>
          <p className="mt-1 break-words">They still work, but they stay {result.from.name}'s. Update, move or remove them there if they need it.</p>
        </div>
      )}

      {result.blocked.length > 0 && (
        <div className="rounded border border-red-200 bg-red-50 p-2 text-red-900 min-w-0">
          <p className="font-medium break-words">
            <AlertTriangle className="inline h-4 w-4 mr-1 -mt-0.5" />
            {plural(result.blocked.length, 'cultivar')} {done ? 'were' : 'will'} not {done ? 'moved' : 'move'}:
          </p>
          <ul className="list-disc pl-5">
            {result.blocked.map(b => (
              <li key={b.id} className="break-words">{b.name || `#${b.id}`}: {b.reasons.join('; ')}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export default function CultivarMovePanel() {
  const [orgs, setOrgs] = useState<MoveOrganisation[] | null>(null);
  const [fromId, setFromId] = useState<number | null>(null);
  const [toId, setToId] = useState<number | null>(null);
  const [cultivars, setCultivars] = useState<MovableCultivar[] | null>(null);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [moveRelated, setMoveRelated] = useState(true);
  const [makePrivate, setMakePrivate] = useState(true);
  const [preview, setPreview] = useState<MoveResult | null>(null);
  const [done, setDone] = useState<MoveResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadOrgs = useCallback(async () => {
    try {
      const res = await cultivarMoveService.organisations();
      if (res.success) setOrgs(res.data);
      else setError(res.error.message);
    } catch (e) {
      setError(errorText(e));
    }
  }, []);

  useEffect(() => { loadOrgs(); }, [loadOrgs]);

  const loadCultivars = useCallback(async (orgId: number) => {
    setCultivars(null);
    try {
      const res = await cultivarMoveService.cultivars(orgId);
      if (res.success) setCultivars(res.data);
      else setError(res.error.message);
    } catch (e) {
      setError(errorText(e));
    }
  }, []);

  useEffect(() => {
    setSelected(new Set());
    setPreview(null);
    if (fromId) loadCultivars(fromId);
    else setCultivars(null);
  }, [fromId, loadCultivars]);

  // Any change to what would be sent makes the preview out of date.
  useEffect(() => { setPreview(null); }, [selected, toId, moveRelated, makePrivate]);

  const shown = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!cultivars) return [];
    if (!term) return cultivars;
    return cultivars.filter(c =>
      c.name.toLowerCase().includes(term)
      || (c.crop_type_name || '').toLowerCase().includes(term)
      || (c.legacy_code || '').toLowerCase().includes(term));
  }, [cultivars, search]);

  const allShownSelected = shown.length > 0 && shown.every(c => selected.has(c.id));

  const toggle = (id: number) => setSelected(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const toggleShown = () => setSelected(prev => {
    const next = new Set(prev);
    if (allShownSelected) shown.forEach(c => next.delete(c.id));
    else shown.forEach(c => next.add(c.id));
    return next;
  });

  const request = () => ({ fromOrgId: fromId!, toOrgId: toId!, ids: [...selected], moveRelated, makePrivate });
  const ready = !!fromId && !!toId && fromId !== toId && selected.size > 0;

  const runPreview = async () => {
    setBusy(true);
    setError(null);
    setDone(null);
    try {
      const res = await cultivarMoveService.preview(request());
      if (res.success) setPreview(res.data);
      else setError(res.error.message);
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  const runMove = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await cultivarMoveService.move(request());
      if (res.success) {
        setDone(res.data);
        setPreview(null);
        setSelected(new Set());
        if (fromId) loadCultivars(fromId);
        loadOrgs();
      } else {
        setError(res.error.message);
      }
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  const fromOrg = orgs?.find(o => o.id === fromId) || null;
  const sources = (orgs || []).filter(o => o.cultivar_count > 0);
  const targets = (orgs || []).filter(o => o.is_active && o.id !== fromId);

  return (
    <div className="space-y-4 min-w-0">
      <p className="text-sm text-neutral-600 break-words">
        For cultivars registered under the wrong organisation. Licences, orders, plantings and stock that name a cultivar stay as they are, and the preview lists them.
      </p>

      {error && <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-800 break-words">{error}</div>}
      {done && <MoveSummary result={done} done />}

      <div className="grid grid-cols-1 sm:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] gap-3 items-end">
        <label className="block min-w-0">
          <span className="block text-sm font-medium text-neutral-700 mb-1">Cultivars are in</span>
          <select
            className="w-full min-w-0 rounded-lg border border-neutral-300 px-3 py-2 text-sm"
            value={fromId ?? ''}
            onChange={e => setFromId(e.target.value ? Number(e.target.value) : null)}
            disabled={!orgs}
          >
            <option value="">{orgs ? 'Choose an organisation' : 'Loading…'}</option>
            {sources.map(o => <option key={o.id} value={o.id}>{o.name} ({o.cultivar_count})</option>)}
          </select>
        </label>
        <ArrowRight className="hidden sm:block h-5 w-5 mb-2.5 text-neutral-400" aria-hidden />
        <label className="block min-w-0">
          <span className="block text-sm font-medium text-neutral-700 mb-1">Move them to</span>
          <select
            className="w-full min-w-0 rounded-lg border border-neutral-300 px-3 py-2 text-sm"
            value={toId ?? ''}
            onChange={e => setToId(e.target.value ? Number(e.target.value) : null)}
            disabled={!orgs}
          >
            <option value="">Choose an organisation</option>
            {targets.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
          </select>
        </label>
      </div>

      {fromId && (
        <div className="rounded-xl border border-neutral-200 bg-white min-w-0">
          <div className="flex flex-wrap items-center gap-3 border-b border-neutral-200 p-3">
            <label className="flex items-center gap-2 text-sm font-medium text-neutral-800">
              <input type="checkbox" className="h-4 w-4" checked={allShownSelected} onChange={toggleShown} disabled={!shown.length} />
              {search ? 'Select all shown' : 'Select all'}
            </label>
            <span className="text-sm text-neutral-600">{selected.size} of {cultivars?.length ?? 0} selected</span>
            <div className="relative ml-auto w-full sm:w-64">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-neutral-400" aria-hidden />
              <input
                type="search"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Name, crop or legacy code"
                aria-label="Search cultivars"
                className="w-full rounded-lg border border-neutral-300 pl-8 pr-3 py-2 text-sm"
              />
            </div>
          </div>
          {!cultivars ? (
            <p className="p-3 text-sm text-neutral-500"><Loader2 className="inline h-4 w-4 mr-1 animate-spin" />Loading cultivars…</p>
          ) : cultivars.length === 0 ? (
            <p className="p-3 text-sm text-neutral-500">{fromOrg?.name} owns no cultivars.</p>
          ) : (
            <ul className="max-h-[28rem] overflow-y-auto divide-y divide-neutral-100">
              {shown.map(c => (
                <li key={c.id}>
                  <label className="flex items-start gap-3 px-3 py-2 text-sm cursor-pointer hover:bg-neutral-50 min-w-0">
                    <input
                      type="checkbox"
                      className="mt-0.5 h-4 w-4 shrink-0"
                      checked={selected.has(c.id)}
                      onChange={() => toggle(c.id)}
                    />
                    <span className="min-w-0">
                      <span className="font-medium text-neutral-900 break-words">{c.name}</span>
                      <span className="block text-xs text-neutral-500 break-words">
                        {[c.crop_type_name, c.legacy_code && `Legacy ${c.legacy_code}`, c.protection_status && c.protection_status !== 'open' ? c.protection_status : null]
                          .filter(Boolean).join(' · ') || 'No crop type'}
                      </span>
                    </span>
                  </label>
                </li>
              ))}
              {shown.length === 0 && <li className="p-3 text-sm text-neutral-500">Nothing matches “{search}”.</li>}
            </ul>
          )}
        </div>
      )}

      {fromId && (
        <label className="flex items-start gap-2 text-sm text-neutral-800">
          <input type="checkbox" className="mt-0.5 h-4 w-4 shrink-0" checked={moveRelated} onChange={e => setMoveRelated(e.target.checked)} />
          <span className="break-words">
            Also move {fromOrg?.name || 'the old organisation'}'s photos, readings, guides, documents and maturity records on these cultivars
          </span>
        </label>
      )}

      {fromId && (
        <label className="flex items-start gap-2 text-sm text-neutral-800">
          <input type="checkbox" className="mt-0.5 h-4 w-4 shrink-0" checked={makePrivate} onChange={e => setMakePrivate(e.target.checked)} />
          <span className="break-words">
            Make any open ones private. An open cultivar is shown to every organisation, so {fromOrg?.name || 'the old organisation'} would still see it
          </span>
        </label>
      )}

      {preview && <MoveSummary result={preview} done={false} />}

      {fromId && (
        <div className="flex flex-wrap gap-2">
          <Button variant="secondary" onClick={runPreview} disabled={!ready || busy}>
            {busy && !preview ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : null}
            Preview the move
          </Button>
          {preview && preview.moved.length > 0 && (
            <Button onClick={runMove} disabled={busy}>
              {busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : null}
              Move {plural(preview.moved.length, 'cultivar')} to {preview.to.name}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
